import {
  CONSENT_CATEGORIES,
  isConsentCategory,
} from './constants.js';
import { MAX_CONSENT_LIFETIME_DAYS } from './cookie-storage.js';
import { isCanonicalSdkVersion } from './sdk-version.js';
import type {
  ConsentCategoryConfiguration,
  ConsentConfiguration,
  ConsentStorageKind,
  ConsentVendorConfiguration,
  ConsentVendorCookie,
} from './types.js';
import {
  isCanonicalPolicyVersion,
  isRecord,
} from './validation.js';

const SITE_ID_PATTERN = /^site_[a-z0-9][a-z0-9_-]{0,63}$/;
const MAX_CATEGORY_LABEL_LENGTH = 200;
const MAX_CATEGORY_DESCRIPTION_LENGTH = 2_000;
const VENDOR_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const STORAGE_KINDS: readonly string[] = ['cookie', 'localStorage', 'sessionStorage'];

export type ConsentConfigurationErrorCode =
  | 'invalid_categories'
  | 'invalid_configuration_version'
  | 'invalid_lifetime'
  | 'invalid_minimum_valid_version'
  | 'invalid_policy_version'
  | 'invalid_sdk_version'
  | 'invalid_site_id'
  | 'invalid_vendors'
  | 'not_an_object'
  | 'site_mismatch';

export type ConsentConfigurationParseResult =
  | { readonly ok: true; readonly config: ConsentConfiguration }
  | {
      readonly ok: false;
      readonly code: ConsentConfigurationErrorCode;
      readonly message: string;
    };

function failure(
  code: ConsentConfigurationErrorCode,
  message: string,
): ConsentConfigurationParseResult {
  return { ok: false, code, message };
}

function isPositiveVersion(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 1;
}

function parseCategory(
  value: unknown,
): ConsentCategoryConfiguration | null {
  if (
    !isRecord(value) ||
    !isConsentCategory(value.key) ||
    typeof value.required !== 'boolean' ||
    typeof value.label !== 'string'
  ) {
    return null;
  }

  const label = value.label.trim();
  if (label.length === 0 || label.length > MAX_CATEGORY_LABEL_LENGTH) {
    return null;
  }

  const shouldBeRequired = value.key === 'necessary';
  if (value.required !== shouldBeRequired) {
    return null;
  }

  if (
    value.description !== undefined &&
    (typeof value.description !== 'string' ||
      value.description.trim().length === 0 ||
      value.description.trim().length > MAX_CATEGORY_DESCRIPTION_LENGTH)
  ) {
    return null;
  }

  const description =
    typeof value.description === 'string' ? value.description.trim() : undefined;

  return Object.freeze({
    key: value.key,
    required: value.required,
    label,
    ...(description === undefined ? {} : { description }),
  });
}

// Mirrors the platform manifest rules; anything else makes the config invalid.
function text(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= max ? trimmed : null;
}

function httpsUrl(value: unknown): string | null {
  const href = text(value, 2_048);
  if (href === null) return null;
  try {
    const url = new URL(href);
    return url.protocol === 'https:' &&
      url.username === '' &&
      url.password === '' &&
      url.hash === '' &&
      !href.includes('#')
      ? href
      : null;
  } catch {
    return null;
  }
}

function parseCookie(value: unknown): ConsentVendorCookie | null {
  if (!isRecord(value)) return null;
  const name = text(value.name, 200);
  const duration = text(value.duration, 200);
  const purpose = value.purpose === undefined ? undefined : text(value.purpose, 1_000);
  if (
    name === null ||
    duration === null ||
    purpose === null ||
    !STORAGE_KINDS.includes(value.kind as string)
  ) {
    return null;
  }
  return Object.freeze({
    name,
    kind: value.kind as ConsentStorageKind,
    duration,
    ...(purpose === undefined ? {} : { purpose }),
  });
}

function parseVendor(value: unknown): ConsentVendorConfiguration | null {
  if (
    !isRecord(value) ||
    typeof value.key !== 'string' ||
    !VENDOR_KEY_PATTERN.test(value.key) ||
    !isConsentCategory(value.category)
  ) {
    return null;
  }
  const name = text(value.name, 200);
  const provider = text(value.provider, 200);
  const purpose = text(value.purpose, 1_000);
  const retention = text(value.retention, 500);
  const policyUrl = value.policyUrl === undefined ? undefined : httpsUrl(value.policyUrl);
  const rawCookies = value.cookies ?? [];
  if (
    name === null ||
    provider === null ||
    purpose === null ||
    retention === null ||
    policyUrl === null ||
    !Array.isArray(rawCookies) ||
    rawCookies.length > 50 ||
    (value.cookies !== undefined && rawCookies.length === 0)
  ) {
    return null;
  }
  const cookies = rawCookies.map(parseCookie);
  if (cookies.includes(null)) return null;

  return Object.freeze({
    key: value.key,
    name,
    provider,
    category: value.category,
    purpose,
    retention,
    ...(policyUrl === undefined ? {} : { policyUrl }),
    cookies: Object.freeze(cookies as ConsentVendorCookie[]),
  });
}

function parseVendors(
  value: unknown,
): readonly ConsentVendorConfiguration[] | null {
  if (value === undefined) return Object.freeze([]);
  if (!Array.isArray(value) || value.length > 100) return null;
  const vendors = value.map(parseVendor);
  if (vendors.includes(null)) return null;
  const keys = new Set(vendors.map((vendor) => vendor?.key));
  return keys.size === vendors.length
    ? Object.freeze(vendors as ConsentVendorConfiguration[])
    : null;
}

function parseCategories(
  value: unknown,
): readonly ConsentCategoryConfiguration[] | null {
  if (!Array.isArray(value) || value.length !== CONSENT_CATEGORIES.length) {
    return null;
  }

  const parsed = value.map(parseCategory);
  if (parsed.some((category) => category === null)) {
    return null;
  }

  const byKey = new Map(
    parsed.map((category) => [category?.key, category] as const),
  );

  if (byKey.size !== CONSENT_CATEGORIES.length) {
    return null;
  }

  const canonical = CONSENT_CATEGORIES.map((key) => byKey.get(key));
  if (canonical.some((category) => category === undefined)) {
    return null;
  }

  return Object.freeze(
    canonical as readonly ConsentCategoryConfiguration[],
  );
}

export function parseConsentConfiguration(
  value: unknown,
  expectedSiteId: string,
): ConsentConfigurationParseResult {
  if (!isRecord(value)) {
    return failure('not_an_object', 'Configuration must be a JSON object.');
  }

  if (
    !SITE_ID_PATTERN.test(expectedSiteId) ||
    typeof value.siteId !== 'string' ||
    !SITE_ID_PATTERN.test(value.siteId)
  ) {
    return failure(
      'invalid_site_id',
      'Expected and received site IDs must use the canonical site_ format.',
    );
  }

  if (value.siteId !== expectedSiteId) {
    return failure(
      'site_mismatch',
      'Configuration site ID does not match the initialized site.',
    );
  }

  if (!isPositiveVersion(value.configurationVersion)) {
    return failure(
      'invalid_configuration_version',
      'Configuration version must be a positive safe integer.',
    );
  }

  if (!isPositiveVersion(value.minimumValidConsentVersion)) {
    return failure(
      'invalid_minimum_valid_version',
      'Minimum valid consent version must be a positive safe integer.',
    );
  }

  if (value.minimumValidConsentVersion > value.configurationVersion) {
    return failure(
      'invalid_minimum_valid_version',
      'Minimum valid consent version cannot exceed configuration version.',
    );
  }

  if (
    !Number.isSafeInteger(value.consentLifetimeDays) ||
    (value.consentLifetimeDays as number) < 1 ||
    (value.consentLifetimeDays as number) > MAX_CONSENT_LIFETIME_DAYS
  ) {
    return failure(
      'invalid_lifetime',
      'Consent lifetime is outside the supported technical range.',
    );
  }

  if (!isCanonicalPolicyVersion(value.policyVersion)) {
    return failure(
      'invalid_policy_version',
      'Policy version must use the supported canonical format.',
    );
  }

  if (
    !isRecord(value.sdk) ||
    typeof value.sdk.minVersion !== 'string' ||
    value.sdk.minVersion.length > 64 ||
    !isCanonicalSdkVersion(value.sdk.minVersion)
  ) {
    return failure(
      'invalid_sdk_version',
      'Minimum SDK version must use canonical semantic version format.',
    );
  }

  const categories = parseCategories(value.categories);
  if (!categories) {
    return failure(
      'invalid_categories',
      'Configuration must contain each fixed v1 category exactly once with canonical required flags.',
    );
  }

  const vendors = parseVendors(value.vendors);
  if (!vendors) {
    return failure(
      'invalid_vendors',
      'Vendors must be a bounded list of unique, well-formed service descriptions.',
    );
  }

  return {
    ok: true,
    config: Object.freeze({
      siteId: value.siteId,
      configurationVersion: value.configurationVersion,
      minimumValidConsentVersion: value.minimumValidConsentVersion,
      consentLifetimeDays: value.consentLifetimeDays as number,
      policyVersion: value.policyVersion,
      sdk: Object.freeze({ minVersion: value.sdk.minVersion }),
      categories,
      vendors,
    }),
  };
}
